import React, { useCallback, useEffect, useState } from 'react'
import { useQuery } from '@apollo/react-hooks'
import { Button, Drawer, Icon, List, notification, Typography } from 'antd'
import fileDownload from 'js-file-download'
import PropTypes from 'prop-types'

import {
  COMPLETED_MENU_CRAWLING_JOBS_FOR_MENU,
  MENU_CRAWLING_JOB,
} from '../../menus/graphql/queries'
import { formatDate } from '../services/formatter'

import LondonDateTime from './london-date-time'
import Spinner from './spinner'

const HistoryItem = ({ completedDate, crawlingStatus }) => (
  <Typography.Text>
    At <LondonDateTime date={completedDate} /> the crawling job event returned
    with
    <Typography.Text strong> {crawlingStatus}</Typography.Text>
    {' status'}
  </Typography.Text>
)

HistoryItem.propTypes = {
  completedDate: PropTypes.string.isRequired,
  crawlingStatus: PropTypes.string.isRequired,
}

const MenuCrawlingHistoryDrawer = ({ menuId, buttonName, buttonSize }) => {
  const [isDrawerVisible, setIsDrawerVisible] = useState(false)
  const [menuCrawlingJobId, setMenuCrawlingJobId] = useState()

  const {
    data: { completedMenuCrawlingJobsForMenu } = {},
    loading: isLoadingCompletedMenuCrawlingJobs,
    refetch: refetchCompletedMenuCrawlingJobs,
  } = useQuery(COMPLETED_MENU_CRAWLING_JOBS_FOR_MENU, {
    variables: { menuId },
    fetchPolicy: 'no-cache',
  })

  const {
    data: { menuCrawlingJob = {} } = {},
    loading: isLoadingMenuCrawlingJob,
    refetch: refetchMenuCrawlingJob,
  } = useQuery(MENU_CRAWLING_JOB, {
    skip: !menuCrawlingJobId,
    variables: { id: menuCrawlingJobId },
    onCompleted() {
      if (!menuCrawlingJobId) return

      fileDownload(
        JSON.stringify(menuCrawlingJob.menuCrawlingResponse.data, null, 2),
        `${menuId}-${formatDate(menuCrawlingJob.completedDate)}.json`,
      )
    },
    onError({ message }) {
      notification.open({
        message: 'Download crawling response failed.',
        description: message,
        type: 'error',
        duration: 0,
      })
    },
    fetchPolicy: 'no-cache',
  })

  useEffect(() => {
    if (!menuCrawlingJobId) return
    refetchMenuCrawlingJob()
  }, [menuCrawlingJobId, refetchMenuCrawlingJob])

  const onButtonClick = useCallback(() => {
    refetchCompletedMenuCrawlingJobs()
    setIsDrawerVisible(true)
  }, [refetchCompletedMenuCrawlingJobs])

  const onClose = useCallback(() => setIsDrawerVisible(false), [])

  return (
    <>
      <Button size={buttonSize} onClick={onButtonClick}>
        {buttonName}
        <Icon type="eye" />
      </Button>
      {isLoadingCompletedMenuCrawlingJobs ? (
        <Spinner />
      ) : (
        <Drawer
          title="Crawling response history"
          placement="right"
          onClose={onClose}
          visible={isDrawerVisible}
          width="50%"
        >
          <List
            size="small"
            itemLayout="horizontal"
            dataSource={completedMenuCrawlingJobsForMenu}
            renderItem={completedMenuCrawlingJob => (
              <List.Item>
                <HistoryItem {...completedMenuCrawlingJob} />
                <Button
                  size="small"
                  loading={
                    isLoadingMenuCrawlingJob &&
                    completedMenuCrawlingJob.id === menuCrawlingJobId
                  }
                  onClick={() =>
                    setMenuCrawlingJobId(completedMenuCrawlingJob.id)
                  }
                >
                  Download
                  <Icon type="download" />
                </Button>
              </List.Item>
            )}
          />
        </Drawer>
      )}
    </>
  )
}

MenuCrawlingHistoryDrawer.propTypes = {
  menuId: PropTypes.string.isRequired,
  buttonName: PropTypes.string.isRequired,
  buttonSize: PropTypes.string,
}

MenuCrawlingHistoryDrawer.defaultProps = {
  buttonSize: 'small',
}

export default MenuCrawlingHistoryDrawer
